import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cn } from "../utils/utils";
import { ChevronDownIcon } from "@radix-ui/react-icons";

// Funkcja pomocnicza do wyciągania numeru itemu
const getItemNumber = (value: string | undefined): number => {
	if (!value) {
		// console.log("[Accordion] getItemNumber: value is undefined/null, returning 0");
		return 0;
	}
	const match = value.match(/item-(\d+)/);
	const result = match && match[1] ? Number.parseInt(match[1], 10) : 0;
	// console.log("[Accordion] getItemNumber:", { value, result, match: match?.[1] });
	return result;
};

const Accordion = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>
>(({ className, onValueChange, value: externalValue, type, ...props }, ref) => {
	// Obsługujemy tylko type="single"
	const isSingle = type === "single";
	
	// Internal state dla uncontrolled component - używamy pustego stringa zamiast undefined
	const [internalValue, setInternalValue] = React.useState<string>("");
	
	// Używamy external value jeśli jest podane (controlled), w przeciwnym razie internal (uncontrolled)
	const isControlled = externalValue !== undefined;
	const currentValue = isControlled ? externalValue : internalValue;
	
	const previousValueRef = React.useRef<string>("");
	const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
	const accordionRef = React.useRef<HTMLDivElement | null>(null);

	const handleValueChange = (newValue: string | Array<string> | undefined): void => {
		console.log("[Accordion] handleValueChange called", {
			newValue,
			currentValue,
			previousValue: previousValueRef.current,
			isSingle,
			isControlled,
		});

		// Jeśli to nie jest single, przekaż dalej bez zmian
		if (!isSingle) {
			// console.log("[Accordion] Not single type, passing through");
			if (onValueChange) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(onValueChange as any)(newValue);
			}
			if (!isControlled) {
				setInternalValue(newValue as string || "");
			}
			return;
		}

		// Dla single type, konwertuj undefined na pusty string
		const singleValue = typeof newValue === "string" ? newValue : "";

		// Czyść poprzedni timeout jeśli istnieje
		if (timeoutRef.current) {
			// console.log("[Accordion] Clearing previous timeout");
			clearTimeout(timeoutRef.current);
		}

		const currentItemNumber = getItemNumber(singleValue);
		const previousItemNumber = getItemNumber(previousValueRef.current);

		// console.log("[Accordion] Item numbers", {
		// 	currentItemNumber,
		// 	previousItemNumber,
		// 	singleValue,
		// 	previousValue: previousValueRef.current,
		// });

		// Jeśli klikamy item niżej na liście (większy numer) i poprzedni był wyżej (mniejszy numer)
		if (singleValue && previousItemNumber > 0 && currentItemNumber > previousItemNumber) {
			// console.log("[Accordion] Need to close previous first", {
			// 	previousItemNumber,
			// 	currentItemNumber,
			// });
			
			// Najpierw zamknij poprzedni item - używamy pustego stringa zamiast undefined
			// console.log("[Accordion] Step 1: Closing previous item");
			if (onValueChange) {
				(onValueChange as (value: string) => void)("");
			}
			if (!isControlled) {
				setInternalValue("");
			}

		// Po zakończeniu animacji zamykania (300ms) otwórz nowy item
		timeoutRef.current = setTimeout(() => {
			// console.log("[Accordion] Step 2: Opening new item", singleValue);
			
			if (onValueChange) {
				(onValueChange as (value: string) => void)(singleValue);
			}
			if (!isControlled) {
				setInternalValue(singleValue);
			}
			previousValueRef.current = singleValue;
			
			// Przenieś focus na bezpieczny element aby zapobiec automatycznemu scrollowaniu
			requestAnimationFrame(() => {
				// console.log("[Accordion] Transferring focus to prevent scroll");
				if (accordionRef.current) {
					// Usuń focus z aktywnego elementu (trigger button)
					const activeElement = document.activeElement as HTMLElement;
					if (activeElement && activeElement !== document.body) {
						// console.log("[Accordion] Blurring active element:", activeElement.tagName);
						activeElement.blur();
					}
					// Opcjonalnie: przenieś focus na główny kontener (bez scrollowania)
					// accordionRef.current.focus({ preventScroll: true });
				}
			});
		}, 400); // Opóźnienie = 200ms animacji + 150ms marginesu dla DOM updates
		} else {
			// Normalna ścieżka - bezpośrednia zmiana
			// console.log("[Accordion] Normal flow: direct change");
			if (onValueChange) {
				(onValueChange as (value: string) => void)(singleValue);
			}
			if (!isControlled) {
				setInternalValue(singleValue);
			}
			previousValueRef.current = singleValue;
		}
	};


	// Synchronizuj previousValueRef z value gdy zmienia się z zewnątrz (controlled component)
	React.useEffect(() => {
		const currentStringValue = typeof currentValue === 'string' ? currentValue : "";
		// console.log("[Accordion] useEffect sync check", {
		// 	isSingle,
		// 	currentStringValue,
		// 	previousValue: previousValueRef.current,
		// 	areDifferent: currentStringValue !== previousValueRef.current,
		// });
		if (isSingle && currentStringValue !== previousValueRef.current) {
			// console.log("[Accordion] Syncing previousValueRef from", previousValueRef.current, "to", currentStringValue);
			previousValueRef.current = currentStringValue;
		}
	}, [currentValue, isSingle]);


	// Cleanup timeout przy unmount
	React.useEffect(() => {
		return (): void => {
			if (timeoutRef.current) {
				// console.log("[Accordion] Component unmounting, clearing timeout");
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	// @ts-expect-error - TypeScript nie może określić union type w runtime, ale handleValueChange obsługuje oba przypadki
	return (
		<AccordionPrimitive.Root
			ref={(node: HTMLDivElement | null): void => {
				accordionRef.current = node;
				if (typeof ref === 'function') {
					ref(node);
				} else if (ref) {
					ref.current = node;
				}
			}}
			className={className}
			value={currentValue}
			type={type}
			onValueChange={handleValueChange}
			{...props}
		/>
	);
});
Accordion.displayName = "Accordion";

const AccordionItem = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
	<AccordionPrimitive.Item
		ref={ref}
		className={cn("", className)}
		{...props}
	/>
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
	<AccordionPrimitive.Header className="flex">
		<AccordionPrimitive.Trigger
			ref={ref}
			className={cn(
				"flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all text-left [&[data-state=open]>svg]:rotate-180 ml-4",
				className
			)}
			{...props}
		>
			{children}
			<ChevronDownIcon className="h-6 w-6 shrink-0 text-muted-foreground transition-transform duration-200 mx-4" />
		</AccordionPrimitive.Trigger>
	</AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionTriggerEnd = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
	<AccordionPrimitive.Trigger
		ref={ref}
		className={cn(
			"py-4 text-sm font-medium transition-all text-center w-full text-slate-400",
			className
		)}
		{...props}
	>
		{children}
	</AccordionPrimitive.Trigger>
));
AccordionTriggerEnd.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<AccordionPrimitive.Content
		ref={ref}
		className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
		{...props}
	>
		<div className={cn("pb-4 pt-0", className)}>{children}</div>
	</AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export {
	Accordion,
	AccordionItem,
	AccordionTrigger,
	AccordionContent,
	AccordionTriggerEnd,
};
